import type { ReactNode } from "react";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import type { homeSuperSplashLivePrimaryContentItemContent$key } from "~/queries/__generated__/homeSuperSplashLivePrimaryContentItemContent.graphql";

import {
  ActionBar,
  Container,
  Content,
  CoverImage,
  Headline,
  HeadlineContainer,
  LinkHighlightArea,
  LiveTagContainer,
  StyledEntityLink,
  SubContentInMobileAndDesktop,
  SubContentInTablet,
  SubHeadline,
} from "./styles";

export type Props = {
  children?: ReactNode;
  reference: homeSuperSplashLivePrimaryContentItemContent$key;
} & ContentItemRenderVariantProps;

export const ContentItemHomeSuperSplashLivePrimary = forwardRef<HTMLDivElement, Props>(
  ({ children, className, onClick, provider, reference: reference_ }, reference) => {
    const content = useFragment(
      graphql`
        fragment homeSuperSplashLivePrimaryContentItemContent on Content {
          ...entityLink
          entityId
        }
      `,
      reference_,
    );

    return (
      <Container className={className} ref={reference}>
        <SubContentInTablet>{children}</SubContentInTablet>
        <StyledEntityLink reference={content} onClick={() => onClick?.(content.entityId)}>
          <CoverImage>
            {provider.coverImage({
              responsiveVariants: {
                desktopUp: "size540x360",
                homeDesktopUp: "size540x360",
                mobileUp: "size320x240",
                tabletUp: "size540x360",
              },
              withCaption: true,
            })}
          </CoverImage>
        </StyledEntityLink>
        <Content>
          <StyledEntityLink reference={content}>
            <LinkHighlightArea>
              <HeadlineContainer>
                <Headline>{provider.headline({ preferSocialHeadline: true })}</Headline>
                <LiveTagContainer>{provider.liveTag()}</LiveTagContainer>
              </HeadlineContainer>
              <SubHeadline>{provider.subHeadline()}</SubHeadline>
            </LinkHighlightArea>
          </StyledEntityLink>
          <ActionBar>
            {provider.actionBar({
              isBookmarkVisible: false,
            })}
          </ActionBar>
          <SubContentInMobileAndDesktop>{children}</SubContentInMobileAndDesktop>
        </Content>
      </Container>
    );
  },
);

ContentItemHomeSuperSplashLivePrimary.displayName = "ContentItemHomeSuperSplashLivePrimary";
